/**
 * 音声録音パネルコンポーネント
 * ブラウザ録音とファイルアップロードの両方をサポート
 */
import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Paper,
  IconButton,
  Alert,
  Chip,
  LinearProgress,
  Divider,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useTheme } from '@mui/material';
import { bg, border, text as textColors } from '../../../../utils/darkModeAdapter';

import useAudioRecorder from '../../hooks/useAudioRecorder';
import ConsentDialog from './ConsentDialog';
import AudioUploader from './AudioUploader';
import AnalysisResultsPanel from '../AnalysisResults/AnalysisResultsPanel';

const AudioRecorderPanel = ({
  checkSheetId,
  onRecordingComplete,
  onUploadComplete,
  onAnalysisComplete,
  audioService,
  disabled = false,
}) => {
  const theme = useTheme();
  const [recordMode, setRecordMode] = useState('record'); // 'record' | 'upload'
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  const [consentLoading, setConsentLoading] = useState(false);
  const [consentError, setConsentError] = useState(null);
  const [consentId, setConsentId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [recordingComplete, setRecordingComplete] = useState(false);
  const [recordingId, setRecordingId] = useState(null);

  // 解析状態
  const [analysisJobId, setAnalysisJobId] = useState(null);
  const [analysisStatus, setAnalysisStatus] = useState(null); // pending, processing, completed, failed
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [analysisStep, setAnalysisStep] = useState('');
  const [analysisError, setAnalysisError] = useState(null);
  const [analysisPolling, setAnalysisPolling] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [resultsLoading, setResultsLoading] = useState(false);

  // 解析オプション
  const [enablePiiMasking, setEnablePiiMasking] = useState(true);

  // 録音フック
  const {
    isRecording,
    isPaused,
    formattedDuration,
    audioLevel,
    error: recordingError,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
  } = useAudioRecorder({
    onError: (err) => console.error('Recording error:', err),
  });

  // モード切り替え
  const handleModeChange = (event, newMode) => {
    if (newMode !== null && !isRecording) {
      setRecordMode(newMode);
    }
  };

  // 同意ダイアログを開く
  const handleOpenConsent = () => {
    setShowConsentDialog(true);
    setConsentError(null);
  };

  // 同意処理
  const handleConsent = async () => {
    setConsentLoading(true);
    setConsentError(null);

    try {
      // 同意をAPIに記録
      if (audioService) {
        const response = await audioService.recordConsent(checkSheetId, {
          consent_given: true,
          consent_method: 'checkbox',  // バックエンドのEnum: checkbox, verbal, signed
        });
        setConsentId(response.id);
      }

      setShowConsentDialog(false);

      // 録音開始
      await startRecording();
    } catch (err) {
      console.error('Consent error:', err);
      setConsentError('同意の記録に失敗しました。もう一度お試しください。');
    } finally {
      setConsentLoading(false);
    }
  };

  // 録音停止
  const handleStopRecording = async () => {
    try {
      const result = await stopRecording();

      if (result && result.blob) {
        setRecordingComplete(true);

        // アップロード
        if (audioService && checkSheetId) {
          setUploading(true);
          setUploadProgress(0);

          const formData = new FormData();
          formData.append('file', result.blob, `recording_${Date.now()}.webm`);

          const uploadResponse = await audioService.uploadAudio(
            checkSheetId,
            formData,
            'browser',
            consentId,
            (progress) => setUploadProgress(progress)
          );

          // 録音IDを保存（解析開始用）
          if (uploadResponse && uploadResponse.id) {
            setRecordingId(uploadResponse.id);
          }

          if (onRecordingComplete) {
            onRecordingComplete(uploadResponse);
          }
        }
      }
    } catch (err) {
      console.error('Stop recording error:', err);
      setUploadError('録音データのアップロードに失敗しました。');
    } finally {
      setUploading(false);
    }
  };

  // 解析開始
  const handleStartAnalysis = async () => {
    if (!recordingId || !audioService) return;

    setAnalysisError(null);
    setAnalysisStatus('pending');
    setAnalysisProgress(0);
    setAnalysisStep('解析を準備中...');

    try {
      // 解析オプションを渡して解析ジョブを開始
      const options = {
        enable_pii_masking: enablePiiMasking,
        enable_acoustic_analysis: true,
        enable_language_analysis: true,
      };
      const response = await audioService.startAnalysis(recordingId, options);
      setAnalysisJobId(response.id);
      setAnalysisStatus('processing');

      // ポーリング開始
      startAnalysisPolling(response.id);
    } catch (err) {
      console.error('Analysis start error:', err);
      setAnalysisError('解析の開始に失敗しました。');
      setAnalysisStatus('failed');
    }
  };

  // 解析状況ポーリング
  const startAnalysisPolling = useCallback((jobId) => {
    setAnalysisPolling(true);

    const pollInterval = setInterval(async () => {
      try {
        const status = await audioService.getAnalysisStatus(jobId);

        setAnalysisProgress(status.progress_percent || 0);
        setAnalysisStep(status.current_step || '処理中...');
        setAnalysisStatus(status.status);

        if (status.status === 'completed') {
          clearInterval(pollInterval);
          setAnalysisPolling(false);
          setAnalysisStep('解析完了');
          // 結果を取得
          fetchAnalysisResults(jobId);
          // 親コンポーネントに完了を通知
          if (onAnalysisComplete) {
            onAnalysisComplete(jobId);
          }
        } else if (status.status === 'failed') {
          clearInterval(pollInterval);
          setAnalysisPolling(false);
          setAnalysisError(status.error_message || '解析に失敗しました。');
        }
      } catch (err) {
        console.error('Polling error:', err);
        clearInterval(pollInterval);
        setAnalysisPolling(false);
        setAnalysisError('解析状況の取得に失敗しました。');
        setAnalysisStatus('failed');
      }
    }, 3000); // 3秒ごとにポーリング

    // クリーンアップ用に保存
    return () => clearInterval(pollInterval);
  }, [audioService]);

  // 解析結果を取得
  const fetchAnalysisResults = async (jobId) => {
    if (!audioService) return;

    setResultsLoading(true);
    try {
      // resultsServiceを使って各結果を取得
      const { resultsService } = await import('../../services/audioAPI');
      const results = await resultsService.getFullResultsByJobId(jobId);
      setAnalysisResults(results);
    } catch (err) {
      console.error('Failed to fetch analysis results:', err);
      setAnalysisError('結果の取得に失敗しました。');
    } finally {
      setResultsLoading(false);
    }
  };

  // ファイルアップロード
  const handleFileUpload = async (file) => {
    if (!audioService || !checkSheetId) return;

    setUploading(true);
    setUploadProgress(0);
    setUploadError(null);

    try {
      // まず同意を記録（ファイルアップロードの場合も）
      let uploadConsentId = consentId;
      if (!uploadConsentId) {
        const consentResponse = await audioService.recordConsent(checkSheetId, {
          consent_given: true,
          consent_method: 'checkbox',  // バックエンドのEnum: checkbox, verbal, signed
        });
        uploadConsentId = consentResponse.id;
        setConsentId(uploadConsentId);
      }

      const formData = new FormData();
      formData.append('file', file);

      const response = await audioService.uploadAudio(
        checkSheetId,
        formData,
        'upload',
        uploadConsentId,
        (progress) => setUploadProgress(progress)
      );

      // 録音IDを保存（解析開始用）
      if (response && response.id) {
        setRecordingId(response.id);
        setRecordingComplete(true);
      }

      if (onUploadComplete) {
        onUploadComplete(response);
      }
    } catch (err) {
      console.error('Upload error:', err);
      setUploadError('アップロードに失敗しました。もう一度お試しください。');
    } finally {
      setUploading(false);
    }
  };

  // 音声レベルインジケーター
  const renderAudioLevel = () => {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
        <GraphicEqIcon sx={{ color: '#249BE0' }} />
        <Box sx={{ flexGrow: 1 }}>
          <LinearProgress
            variant="determinate"
            value={audioLevel}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: border(theme).light,
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                backgroundColor: audioLevel > 70 ? '#22c55e' : audioLevel > 30 ? '#249BE0' : '#94a3b8',
                transition: 'transform 0.1s ease',
              },
            }}
          />
        </Box>
        <Typography variant="caption" sx={{ color: textColors(theme).secondary, minWidth: 40 }}>
          {Math.round(audioLevel)}%
        </Typography>
      </Box>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${border(theme).light}`,
        borderRadius: 3,
        overflow: 'hidden',
      }}
    >
      {/* モード選択ヘッダー */}
      <Box sx={{
        p: 2,
        borderBottom: `1px solid ${border(theme).light}`,
        backgroundColor: bg(theme).subtle,
        display: 'flex',
        justifyContent: 'center'
      }}>
        <ToggleButtonGroup
          value={recordMode}
          exclusive
          onChange={handleModeChange}
          size="small"
          disabled={isRecording}
          sx={{
            '& .MuiToggleButton-root': {
              px: 3,
              py: 1,
              textTransform: 'none',
              fontWeight: 500,
              borderColor: border(theme).light,
              '&.Mui-selected': {
                backgroundColor: '#249BE0',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#1976d2'
                }
              },
              '&.Mui-disabled': {
                color: textColors(theme).muted
              }
            }
          }}
        >
          <ToggleButton value="record">
            <MicIcon sx={{ mr: 1, fontSize: 20 }} />
            マイク録音
          </ToggleButton>
          <ToggleButton value="upload">
            <CloudUploadIcon sx={{ mr: 1, fontSize: 20 }} />
            ファイル選択
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* タブコンテンツ */}
      <Box sx={{ p: 3 }}>
        {/* エラー表示 */}
        {(recordingError || uploadError) && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {recordingError || uploadError}
          </Alert>
        )}

        {/* マイク録音モード */}
        {recordMode === 'record' && (
          <Box sx={{ textAlign: 'center' }}>
            {!isRecording && !recordingComplete && (
              <>
                <Typography variant="body2" sx={{ color: textColors(theme).secondary, mb: 3 }}>
                  マイクを使用して面接を録音します。録音を開始すると、候補者との会話が記録されます。
                </Typography>

                <Button
                  variant="contained"
                  size="large"
                  startIcon={<MicIcon />}
                  onClick={handleOpenConsent}
                  disabled={disabled}
                  sx={{
                    borderRadius: 3,
                    px: 4,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    backgroundColor: '#249BE0',
                    '&:hover': {
                      backgroundColor: '#1976d2',
                    },
                  }}
                >
                  録音を開始
                </Button>
              </>
            )}

            {isRecording && (
              <Box>
                {/* 録音中インジケーター */}
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 2 }}>
                  <FiberManualRecordIcon
                    sx={{
                      color: isPaused ? '#94a3b8' : '#ef4444',
                      animation: isPaused ? 'none' : 'pulse 1s infinite',
                      '@keyframes pulse': {
                        '0%': { opacity: 1 },
                        '50%': { opacity: 0.5 },
                        '100%': { opacity: 1 },
                      },
                    }}
                  />
                  <Chip
                    label={isPaused ? '一時停止中' : '録音中'}
                    color={isPaused ? 'default' : 'error'}
                    size="small"
                  />
                </Box>

                {/* 録音時間 */}
                <Typography
                  variant="h3"
                  sx={{
                    fontFamily: 'monospace',
                    fontWeight: 600,
                    color: textColors(theme).primary,
                    mb: 2,
                  }}
                >
                  {formattedDuration}
                </Typography>

                {/* 音声レベル */}
                {!isPaused && renderAudioLevel()}

                {/* コントロールボタン */}
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
                  {isPaused ? (
                    <IconButton
                      onClick={resumeRecording}
                      sx={{
                        backgroundColor: '#249BE0',
                        color: 'white',
                        width: 56,
                        height: 56,
                        '&:hover': {
                          backgroundColor: '#1976d2',
                        },
                      }}
                    >
                      <PlayArrowIcon fontSize="large" />
                    </IconButton>
                  ) : (
                    <IconButton
                      onClick={pauseRecording}
                      sx={{
                        backgroundColor: '#f59e0b',
                        color: 'white',
                        width: 56,
                        height: 56,
                        '&:hover': {
                          backgroundColor: '#d97706',
                        },
                      }}
                    >
                      <PauseIcon fontSize="large" />
                    </IconButton>
                  )}

                  <IconButton
                    onClick={handleStopRecording}
                    sx={{
                      backgroundColor: '#ef4444',
                      color: 'white',
                      width: 56,
                      height: 56,
                      '&:hover': {
                        backgroundColor: '#dc2626',
                      },
                    }}
                  >
                    <StopIcon fontSize="large" />
                  </IconButton>
                </Box>

                <Typography variant="caption" sx={{ display: 'block', mt: 2, color: textColors(theme).muted }}>
                  5分ごとに自動保存されます
                </Typography>
              </Box>
            )}

            {recordingComplete && !isRecording && (
              <Box>
                {uploading ? (
                  <>
                    <Typography variant="body1" sx={{ color: textColors(theme).primary, mb: 2 }}>
                      録音データをアップロード中...
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={uploadProgress}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: border(theme).light,
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 4,
                          backgroundColor: '#249BE0',
                        },
                      }}
                    />
                    <Typography variant="caption" sx={{ color: textColors(theme).secondary, mt: 1 }}>
                      {uploadProgress}%
                    </Typography>
                  </>
                ) : (
                  <>
                    {/* 解析状況に応じた表示 */}
                    {!analysisStatus && (
                      <>
                        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                          録音が完了しました。解析を開始できます。
                        </Alert>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                          <Button
                            variant="contained"
                            startIcon={<AnalyticsIcon />}
                            onClick={handleStartAnalysis}
                            disabled={!recordingId}
                            sx={{
                              backgroundColor: '#22c55e',
                              textTransform: 'none',
                              borderRadius: 2,
                              fontWeight: 600,
                              '&:hover': {
                                backgroundColor: '#16a34a',
                              },
                            }}
                          >
                            解析を開始
                          </Button>
                          <Button
                            variant="outlined"
                            onClick={() => {
                              setRecordingComplete(false);
                              setConsentId(null);
                              setRecordingId(null);
                              setAnalysisStatus(null);
                            }}
                            sx={{
                              borderColor: '#249BE0',
                              color: '#249BE0',
                              textTransform: 'none',
                              borderRadius: 2,
                            }}
                          >
                            新しい録音を開始
                          </Button>
                        </Box>
                      </>
                    )}

                    {/* 解析中 */}
                    {(analysisStatus === 'pending' || analysisStatus === 'processing' ||
                      analysisStatus === 'transcribing' || analysisStatus === 'analyzing_acoustic' ||
                      analysisStatus === 'analyzing_language' || analysisStatus === 'scoring') && (
                      <Box>
                        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                          <Typography variant="body2" fontWeight={600}>
                            音声解析中...
                          </Typography>
                          <Typography variant="caption">
                            {analysisStep}
                          </Typography>
                        </Alert>
                        <LinearProgress
                          variant="determinate"
                          value={analysisProgress}
                          sx={{
                            height: 10,
                            borderRadius: 5,
                            backgroundColor: border(theme).light,
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 5,
                              backgroundColor: '#249BE0',
                            },
                          }}
                        />
                        <Typography variant="caption" sx={{ color: textColors(theme).secondary, mt: 1, display: 'block' }}>
                          {analysisProgress}% 完了
                        </Typography>
                      </Box>
                    )}

                    {/* 解析完了 */}
                    {analysisStatus === 'completed' && (
                      <Box>
                        <Alert
                          severity="success"
                          icon={<CheckCircleIcon />}
                          sx={{ mb: 2, borderRadius: 2 }}
                        >
                          <Typography variant="body2" fontWeight={600}>
                            解析が完了しました！
                          </Typography>
                          <Typography variant="caption">
                            結果は下部に表示されます。
                          </Typography>
                        </Alert>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setRecordingComplete(false);
                            setConsentId(null);
                            setRecordingId(null);
                            setAnalysisStatus(null);
                            setAnalysisJobId(null);
                          }}
                          sx={{
                            borderColor: '#249BE0',
                            color: '#249BE0',
                            textTransform: 'none',
                            borderRadius: 2,
                          }}
                        >
                          新しい録音を開始
                        </Button>
                      </Box>
                    )}

                    {/* 解析失敗 */}
                    {analysisStatus === 'failed' && (
                      <Box>
                        <Alert
                          severity="error"
                          icon={<ErrorIcon />}
                          sx={{ mb: 2, borderRadius: 2 }}
                        >
                          <Typography variant="body2" fontWeight={600}>
                            解析に失敗しました
                          </Typography>
                          <Typography variant="caption">
                            {analysisError || 'エラーが発生しました。もう一度お試しください。'}
                          </Typography>
                        </Alert>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                          <Button
                            variant="contained"
                            startIcon={<AnalyticsIcon />}
                            onClick={handleStartAnalysis}
                            sx={{
                              backgroundColor: '#22c55e',
                              textTransform: 'none',
                              borderRadius: 2,
                              fontWeight: 600,
                              '&:hover': {
                                backgroundColor: '#16a34a',
                              },
                            }}
                          >
                            再試行
                          </Button>
                          <Button
                            variant="outlined"
                            onClick={() => {
                              setRecordingComplete(false);
                              setConsentId(null);
                              setRecordingId(null);
                              setAnalysisStatus(null);
                              setAnalysisError(null);
                            }}
                            sx={{
                              borderColor: '#249BE0',
                              color: '#249BE0',
                              textTransform: 'none',
                              borderRadius: 2,
                            }}
                          >
                            新しい録音を開始
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* ファイルアップロードモード */}
        {recordMode === 'upload' && (
          <>
            {/* アップロード完了前 */}
            {!recordingComplete && (
              <AudioUploader
                onUpload={handleFileUpload}
                uploading={uploading}
                uploadProgress={uploadProgress}
                error={uploadError}
                disabled={disabled}
              />
            )}

            {/* アップロード完了後 - 解析ボタン表示 */}
            {recordingComplete && !analysisStatus && (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Alert
                  severity="success"
                  sx={{ mb: 3, borderRadius: 2 }}
                >
                  ファイルのアップロードが完了しました。解析を開始できます。
                </Alert>

                {/* 解析オプション */}
                <Box
                  sx={{
                    mb: 3,
                    p: 2,
                    backgroundColor: bg(theme).subtle,
                    borderRadius: 2,
                    border: `1px solid ${border(theme).light}`,
                    textAlign: 'left',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 1,
                      color: textColors(theme).secondary,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <SecurityIcon sx={{ fontSize: 18 }} />
                    解析オプション
                  </Typography>
                  <Tooltip
                    title="有効にすると、書き起こしテキストから個人情報（氏名、会社名、学校名など）を自動的にマスキングしてDBに保存します"
                    placement="top"
                    arrow
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={enablePiiMasking}
                          onChange={(e) => setEnablePiiMasking(e.target.checked)}
                          sx={{
                            color: '#249BE0',
                            '&.Mui-checked': {
                              color: '#249BE0',
                            },
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ color: '#334155' }}>
                          個人情報をマスキングする（推奨）
                        </Typography>
                      }
                    />
                  </Tooltip>
                  {!enablePiiMasking && (
                    <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
                      <Typography variant="caption">
                        マスキングを無効にすると、書き起こしテキストに個人情報がそのまま保存されます
                      </Typography>
                    </Alert>
                  )}
                </Box>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    startIcon={<AnalyticsIcon />}
                    onClick={handleStartAnalysis}
                    disabled={!recordingId}
                    sx={{
                      backgroundColor: '#22c55e',
                      textTransform: 'none',
                      borderRadius: 2,
                      fontWeight: 600,
                      px: 4,
                      '&:hover': {
                        backgroundColor: '#16a34a',
                      },
                    }}
                  >
                    解析を開始
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setRecordingComplete(false);
                      setRecordingId(null);
                      setUploadProgress(0);
                    }}
                    sx={{
                      borderColor: '#249BE0',
                      color: '#249BE0',
                      textTransform: 'none',
                      borderRadius: 2,
                    }}
                  >
                    別のファイルを選択
                  </Button>
                </Box>
              </Box>
            )}

            {/* 解析中 - すべての処理中ステータスに対応 */}
            {recordingComplete && (analysisStatus === 'pending' || analysisStatus === 'processing' ||
              analysisStatus === 'transcribing' || analysisStatus === 'analyzing_acoustic' ||
              analysisStatus === 'analyzing_language' || analysisStatus === 'scoring') && (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                  <Typography variant="body2" fontWeight={600}>
                    音声解析中...
                  </Typography>
                  <Typography variant="caption">
                    {analysisStep || '処理中...'}
                  </Typography>
                </Alert>
                <LinearProgress
                  variant="determinate"
                  value={analysisProgress}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    backgroundColor: border(theme).light,
                    mb: 1,
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 5,
                      backgroundColor: '#249BE0',
                    },
                  }}
                />
                <Typography variant="caption" sx={{ color: textColors(theme).secondary }}>
                  {analysisProgress}% 完了
                </Typography>
              </Box>
            )}

            {/* 解析完了 */}
            {recordingComplete && analysisStatus === 'completed' && (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Alert
                  severity="success"
                  icon={<CheckCircleIcon />}
                  sx={{ mb: 2, borderRadius: 2 }}
                >
                  解析が完了しました！
                </Alert>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setRecordingComplete(false);
                    setRecordingId(null);
                    setUploadProgress(0);
                    setAnalysisStatus(null);
                    setAnalysisResults(null);
                  }}
                  sx={{
                    borderColor: '#249BE0',
                    color: '#249BE0',
                    textTransform: 'none',
                    borderRadius: 2,
                  }}
                >
                  新しいファイルをアップロード
                </Button>
              </Box>
            )}

            {/* 解析失敗 */}
            {recordingComplete && analysisStatus === 'failed' && (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Alert
                  severity="error"
                  sx={{ mb: 2, borderRadius: 2 }}
                >
                  解析に失敗しました。{analysisError}
                </Alert>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    startIcon={<AnalyticsIcon />}
                    onClick={handleStartAnalysis}
                    sx={{
                      backgroundColor: '#22c55e',
                      textTransform: 'none',
                      borderRadius: 2,
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: '#16a34a',
                      },
                    }}
                  >
                    再試行
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setRecordingComplete(false);
                      setRecordingId(null);
                      setUploadProgress(0);
                      setAnalysisStatus(null);
                      setAnalysisError(null);
                    }}
                    sx={{
                      borderColor: '#249BE0',
                      color: '#249BE0',
                      textTransform: 'none',
                      borderRadius: 2,
                    }}
                  >
                    別のファイルを選択
                  </Button>
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* 同意ダイアログ */}
      <ConsentDialog
        open={showConsentDialog}
        onClose={() => setShowConsentDialog(false)}
        onConsent={handleConsent}
        loading={consentLoading}
        error={consentError}
      />

      {/* 解析結果表示 */}
      {(analysisStatus === 'completed' || analysisResults) && (
        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 3 }} />
          <AnalysisResultsPanel
            analysisResults={analysisResults}
            isLoading={resultsLoading}
            error={analysisError}
          />
        </Box>
      )}
    </Paper>
  );
};

export default AudioRecorderPanel;
